// TelcoTrack — výchozí metadata polygonu při importu (Specifikace: Identifikace stavby + Stavební deník).
// Zrcadlí výchozí hodnoty z web `polygon-specification-editor` při načtení / uložení.

import type { PolygonDiaryConfig, PolygonMetadata } from '../interfaces/polygon.interface';

/** Výchozí stavebník — stejně jako ve specifikaci polygonu a stavebním deníku. */
export const DEFAULT_POLYGON_DIARY_STAVEBNIK = 'Cetin a.s.';

/** Výchozí zhotovitel — stejně jako ve specifikaci polygonu a stavebním deníku. */
export const DEFAULT_POLYGON_DIARY_ZHOTOVITEL = 'ProTelco s.r.o.';

/**
 * Minimální uzavřený obdélník kolem centra Prahy.
 * `boundary` je v DB povinné — GeoJSON import i „Nový polygon“ bez geometrie
 * použijí tento stub; po importu objektů se hranice přepočítá (recalculate-boundary).
 */
export const PLACEHOLDER_POLYGON_BOUNDARY: {
  type: 'Polygon';
  coordinates: number[][][];
} = {
  type: 'Polygon',
  coordinates: [[
    [14.4378, 50.0755],
    [14.4478, 50.0755],
    [14.4478, 50.0855],
    [14.4378, 50.0855],
    [14.4378, 50.0755],
  ]],
};

export interface PolygonImportProjectTrackingInput {
  sapConstruction?: string;
  projectState?: string;
}

export interface PolygonImportSpecificationInput {
  /** Název stavby (`polygons.name`). */
  name: string;
  /** Pole z Identifikace stavby uložená v `metadata.projectTracking`. */
  projectTracking?: PolygonImportProjectTrackingInput;
  /** Volitelné přepsání polí stavebního deníku z importu (jinak výchozí hodnoty). */
  diary?: Partial<Pick<
    PolygonDiaryConfig,
    'stavebnik' | 'projektant' | 'zhotovitel' | 'stavebniPovoleniCislo' | 'stavbyveduciName' | 'obec' | 'registracniCisloProjektu'
  >>;
}

function trimOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Sestaví `metadata` pro nově vytvořený polygon při GeoJSON importu.
 * Identifikace stavby (sloupce) se posílají zvlášť v create DTO; zde se doplní stavební deník
 * a prázdná administrativní data pro obec (doplní boundary recalc).
 */
export function buildPolygonImportMetadata(
  input: PolygonImportSpecificationInput,
): Partial<PolygonMetadata> {
  const name = trimOrEmpty(input.name);
  const diaryOverrides = input.diary ?? {};

  const stavebnik = trimOrEmpty(diaryOverrides.stavebnik) || DEFAULT_POLYGON_DIARY_STAVEBNIK;
  const zhotovitel = trimOrEmpty(diaryOverrides.zhotovitel) || DEFAULT_POLYGON_DIARY_ZHOTOVITEL;
  const projektant = trimOrEmpty(diaryOverrides.projektant);
  const stavebniPovoleniCislo = trimOrEmpty(diaryOverrides.stavebniPovoleniCislo);
  const stavbyveduciName = trimOrEmpty(diaryOverrides.stavbyveduciName);
  const obec = trimOrEmpty(diaryOverrides.obec);
  const registracniCisloProjektu = trimOrEmpty(diaryOverrides.registracniCisloProjektu);

  const diaryConfig: PolygonDiaryConfig = {
    constructionName: name,
    stavebnik,
    zhotovitel,
    // Parita s `buildDiaryConfig()` ve spec editoru — prázdné řetězce neukládáme kromě stavbyvedoucího.
    stavbyveduciName,
  };
  if (projektant) diaryConfig.projektant = projektant;
  if (stavebniPovoleniCislo) diaryConfig.stavebniPovoleniCislo = stavebniPovoleniCislo;
  if (registracniCisloProjektu) diaryConfig.registracniCisloProjektu = registracniCisloProjektu;

  const metadata: Partial<PolygonMetadata> = {
    diaryConfig,
    administrativeData: {
      obec,
    },
  };

  const trackingInput = input.projectTracking ?? {};
  const sapConstruction = trimOrEmpty(trackingInput.sapConstruction);
  const projectState = trimOrEmpty(trackingInput.projectState);
  if (sapConstruction || projectState) {
    metadata.projectTracking = {};
    if (sapConstruction) metadata.projectTracking.sapConstruction = sapConstruction;
    if (projectState) metadata.projectTracking.projectState = projectState;
  }

  return metadata;
}
